import React, { type ComponentType, useContext, useEffect, useRef, useState } from 'react';
import PatientPicker from '@components/Patient/PatientPicker';
import { useAppointmentTypes } from '@features/appointment-types/store/hooks';
import type { AppointmentType } from '@features/appointment-types/types';
import { getRecurrenceText } from '@features/appointments/utils/dateTimeFormatters';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import { useForms } from '@features/forms';
import { useClinicians } from '@features/organizations';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { useDebounce } from '@hooks/useDebounce';
import AssignmentOutlined from '@mui/icons-material/AssignmentOutlined';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import EventOutlined from '@mui/icons-material/EventOutlined';
import LocalHospitalOutlined from '@mui/icons-material/LocalHospitalOutlined';
import NotificationsOutlined from '@mui/icons-material/NotificationsOutlined';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import RepeatOutlined from '@mui/icons-material/RepeatOutlined';
import StickyNote2Outlined from '@mui/icons-material/StickyNote2Outlined';
import TimerOutlined from '@mui/icons-material/TimerOutlined';
import VideocamOutlined from '@mui/icons-material/VideocamOutlined';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  InputBase,
  Stack,
  type SvgIconProps,
  TextField,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import type { FormWithRevisionSummary } from '@store/generated/forms';
import { formatAppointmentDuration } from '@utils/durationUtils';
import { format, parseISO } from 'date-fns';
import { useSnackbar } from 'notistack';
import { RRule, rrulestr } from 'rrule';

import type { Patient as PatientRecord } from '../../../../types/types';
import { useAppDispatch } from '@store/hooks';
import { useOrgScheduleAccess } from '../../hooks/useOrgScheduleAccess';
import { useProviderNameMap } from '../../hooks/useProviderNameMap';
import {
  clearOptimisticAppointmentPatch,
  setOptimisticAppointmentPatch,
  useReassignAppointmentMutation,
  useTransitionAppointmentStatusMutation,
  useUpdateAppointmentMutation,
  useUpdateAppointmentSeriesMutation,
  useUpdateAppointmentVisitStatusMutation,
} from '../../store';
import { getVisitStatusDisplay, VISIT_STATUSES, type VisitStatus } from '../../theme/visitStatus';
import type { Appointment, AppointmentUpdate } from '../../types';

const COMMON_DURATIONS = [15, 20, 30, 45, 50, 60] as const;
const DEFAULT_FORM_OFFSET = '-P1D';
const DEFAULT_REMINDER_OFFSET = '-P1D';
const PROPERTY_EDITOR_OPTION_LIST_MAX_HEIGHT = 280;

const FORM_TIMING_PRESETS = [
  { display: '2 hours before appointment', iso: '-PT2H' },
  { display: '1 day before appointment', iso: '-P1D' },
  { display: '1 week before appointment', iso: '-P7D' },
  { display: '5 minutes after appointment', iso: 'PT5M' },
] as const;

const REMINDER_TIMING_PRESETS = [
  { display: '2 hours before appointment', iso: '-PT2H' },
  { display: '1 day before appointment', iso: '-P1D' },
  { display: '1 week before appointment', iso: '-P7D' },
] as const;

const APPOINTMENT_REMINDER = {
  id: 'appointment-reminder',
  reminderTemplateId: 'xxx',
  title: 'Appointment Reminder',
} as const;

const RECURRENCE_PRESETS = [
  { label: 'One-time', value: 'none' },
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Every 2 weeks', value: 'biweekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Yearly', value: 'yearly' },
] as const;

type AppointmentFormRule = NonNullable<AppointmentUpdate['form_rules']>[number];
type AppointmentReminderRule = NonNullable<AppointmentUpdate['reminder_rules']>[number];
type RecurrencePresetValue = (typeof RECURRENCE_PRESETS)[number]['value'] | 'custom';
type TimingPreset = { display: string; iso: string };
type DisplayAppointmentStatus =
  | 'scheduled'
  | 'confirmed'
  | 'cancelled'
  | 'reschedule_requested';

interface AttachedRuleItem {
  id: string;
  title: string;
  offset: string;
  timing: string;
  formDefinitionId?: string;
  reminderTemplateId?: string;
  ruleId?: string | null;
}

export type AppointmentPropertyRowKey =
  | 'status'
  | 'visit_status'
  | 'provider'
  | 'patient'
  | 'type'
  | 'telehealth'
  | 'time'
  | 'duration'
  | 'recurrence'
  | 'forms'
  | 'reminders'
  | 'notes';

interface AppointmentPropertiesTableProps {
  appointment: Appointment;
  rows?: AppointmentPropertyRowKey[];
  interactive?: boolean;
  interactiveRows?: AppointmentPropertyRowKey[];
  editorMode?: 'floating' | 'inline';
  onEditorInteraction?: () => void;
}

const AppointmentPropertyEditorInteractionContext = React.createContext<(() => void) | null>(null);

export const AppointmentPropertiesTable = ({
  appointment,
  rows,
  interactive = false,
  interactiveRows,
  editorMode = 'floating',
  onEditorInteraction,
}: AppointmentPropertiesTableProps) => {
  const showRow = (key: AppointmentPropertyRowKey) => !rows || rows.includes(key);
  const isRowInteractive = (key: AppointmentPropertyRowKey) =>
    interactive || interactiveRows?.includes(key) === true;
  const { hasOrgScheduleAccess } = useOrgScheduleAccess();
  const { clinicians, isLoading: isLoadingClinicians } = useClinicians({
    skip: !hasOrgScheduleAccess,
  });
  const dispatch = useAppDispatch();
  const providerNameMap = useProviderNameMap();
  const { enqueueSnackbar } = useSnackbar();
  const [updateAppointment, { isLoading: isUpdatingAppointment }] = useUpdateAppointmentMutation();
  const [updateAppointmentSeries, { isLoading: isUpdatingSeries }] =
    useUpdateAppointmentSeriesMutation();
  const [optimisticType, setOptimisticType] = useState<{
    id: string | null;
    label: string | null;
  } | null>(null);
  useEffect(() => {
    setOptimisticType(null);
  }, [appointment.appointment_id]);
  const providerName =
    getAppointmentProviderName(appointment) ??
    providerNameMap.get(appointment.provider_id ?? '') ??
    lookupProviderName(appointment.provider_id, clinicians);
  const visitStatus = getVisitStatusDisplay(
    appointment.no_showed ? 'no_show' : appointment.visit_status,
  );
  const status = getStatusDisplay(appointment);
  const start = new Date(appointment.start_time);
  const forms = mapFormRules(appointment.form_rules);
  const reminders = mapReminderRules(appointment.reminder_rules);
  const typeLabel =
    optimisticType?.label ?? appointment.encounter_type ?? appointment.title ?? null;
  const appointmentProviderId =
    appointment.provider_id ?? appointment.provider?.user_id ?? undefined;
  const recurrenceValue = getRecurrencePresetValue(appointment.series?.rrule);
  const isSavingRecurrence = isUpdatingAppointment || isUpdatingSeries;
  const persistAppointmentUpdate = (
    updates: AppointmentUpdate,
    optimisticAppointmentPatch?: Partial<Appointment>,
  ) => {
    dispatch(
      setOptimisticAppointmentPatch({
        appointmentId: appointment.appointment_id,
        patch: {
          ...updates,
          ...optimisticAppointmentPatch,
        } as Partial<Appointment>,
      }),
    );

    return updateAppointment({
      appointmentId: appointment.appointment_id,
      appointmentUpdate: updates,
    })
      .unwrap()
      .catch((error: unknown) => {
        dispatch(clearOptimisticAppointmentPatch(appointment.appointment_id));
        throw error;
      });
  };
  const persistRecurrenceUpdate = (value: RecurrencePresetValue) => {
    if (value === 'custom') return Promise.resolve();

    if (value === 'none') {
      if (appointment.series?.series_id) {
        return persistAppointmentUpdate(
          { detach_from_series: true },
          { series_id: null, series: null },
        );
      }
      return Promise.resolve();
    }

    const rrule = getRRuleForRecurrencePreset(value);
    if (!rrule) return Promise.resolve();

    if (appointment.series?.series_id) {
      return updateAppointmentSeries({
        seriesId: appointment.series.series_id,
        appointmentSeriesUpdate: { rrule },
      }).unwrap();
    }

    return persistAppointmentUpdate({ rrule, create_series: true });
  };
  const handleUpdateFailure = (label: string, error: unknown) => {
    console.error(`Failed to update appointment ${label}:`, error);
    enqueueSnackbar(`Failed to update ${label}. Please try again.`, { variant: 'error' });
  };

  return (
    <AppointmentPropertyEditorInteractionContext.Provider value={onEditorInteraction ?? null}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {showRow('status') && (
        isRowInteractive('status') ? (
          <StatusPropertyRow appointment={appointment} editorMode={editorMode} />
        ) : (
          <PropertyRow icon={CheckCircleOutlined} label="Status">
            <StatusValue color={status.color} label={status.label} />
          </PropertyRow>
        )
      )}

      {showRow('visit_status') &&
        (isRowInteractive('visit_status') ? (
          <VisitStatusPropertyRow appointment={appointment} editorMode={editorMode} />
        ) : (
          <PropertyRow icon={visitStatus.icon} label="Visit status">
            <DotValue color={visitStatus.color} label={visitStatus.label} />
          </PropertyRow>
        ))}

      {showRow('provider') && (
        <ProviderPropertyRow
          appointment={appointment}
          providerName={providerName}
          clinicians={clinicians}
          isLoadingClinicians={isLoadingClinicians}
          disabled={!isRowInteractive('provider') || !hasOrgScheduleAccess}
          editorMode={editorMode}
        />
      )}

      {showRow('patient') && (
        <PatientPropertyRow
          appointment={appointment}
          disabled={!isRowInteractive('patient')}
          editorMode={editorMode}
          onUpdate={persistAppointmentUpdate}
        />
      )}

      {showRow('type') && (
        <EditablePropertyRow
          icon={LocalHospitalOutlined}
          label="Type"
          disabled={!isRowInteractive('type')}
          editorMode={editorMode}
          value={
            typeLabel ? <PlainValue>{typeLabel}</PlainValue> : <EmptyValue>Set type...</EmptyValue>
          }
          popoverWidth={280}
          editor={({ onClose }) => (
            <AppointmentTypeEditor
              value={appointment.encounter_type_id ?? null}
              providerId={appointmentProviderId}
              isSaving={isUpdatingAppointment}
              onChange={(updates, selectedType) => {
                const previousType = optimisticType;
                setOptimisticType({
                  id: selectedType.encounter_type_id ?? null,
                  label: selectedType.encounter_type,
                });

                void persistAppointmentUpdate(updates, {
                  encounter_type_id: selectedType.encounter_type_id ?? null,
                  encounter_type: selectedType.encounter_type,
                  title: selectedType.encounter_type,
                  color: selectedType.color,
                  ...(selectedType.default_duration
                    ? { duration: selectedType.default_duration }
                    : {}),
                  ...(selectedType.telehealth != null
                    ? { telehealth: selectedType.telehealth }
                    : {}),
                })
                  .then(onClose)
                  .catch((error: unknown) => {
                    setOptimisticType(previousType);
                    console.error('Failed to update appointment type:', error);
                    enqueueSnackbar('Failed to update appointment type. Please try again.', {
                      variant: 'error',
                    });
                  });
              }}
            />
          )}
        />
      )}

      {showRow('telehealth') && (
        <EditablePropertyRow
          icon={VideocamOutlined}
          label="Telehealth"
          disabled={!isRowInteractive('telehealth')}
          editorMode={editorMode}
          value={<PlainValue>{appointment.telehealth ? 'Yes' : 'In-person'}</PlainValue>}
          editor={({ onClose }) => (
            <ChoiceEditor
              isSaving={isUpdatingAppointment}
              options={[
                { label: 'Yes', value: true },
                { label: 'In-person', value: false },
              ]}
              value={appointment.telehealth === true}
              onChange={telehealth => {
                void persistAppointmentUpdate({ telehealth })
                  .then(onClose)
                  .catch((error: unknown) => handleUpdateFailure('telehealth', error));
              }}
            />
          )}
        />
      )}

      {showRow('time') && (
        <EditablePropertyRow
          icon={EventOutlined}
          label="Date/time"
          disabled={!isRowInteractive('time')}
          editorMode={editorMode}
          value={<PlainValue>{format(start, 'EEE MMM d · h:mm a')}</PlainValue>}
          popoverWidth={280}
          editor={({ onClose }) => (
            <TimeEditor
              value={appointment.start_time}
              isSaving={isUpdatingAppointment}
              onClose={onClose}
              onChange={startTime => {
                void persistAppointmentUpdate({ start_time: startTime }).catch((error: unknown) =>
                  handleUpdateFailure('date/time', error),
                );
              }}
            />
          )}
        />
      )}

      {showRow('duration') && (
        <EditablePropertyRow
          icon={TimerOutlined}
          label="Duration"
          disabled={!isRowInteractive('duration')}
          editorMode={editorMode}
          value={<PlainValue>{appointment.duration} min</PlainValue>}
          popoverWidth={260}
          editor={({ onClose }) => (
            <DurationEditor
              value={appointment.duration}
              startTime={appointment.start_time}
              isSaving={isUpdatingAppointment}
              onChange={duration => {
                void persistAppointmentUpdate({ duration }).catch((error: unknown) =>
                  handleUpdateFailure('duration', error),
                );
              }}
              onClose={onClose}
            />
          )}
        />
      )}

      {showRow('recurrence') && (
        <EditablePropertyRow
          icon={RepeatOutlined}
          label="Recurrence"
          disabled={!isRowInteractive('recurrence')}
          editorMode={editorMode}
          value={
            appointment.series?.rrule ? (
              <PlainValue>{getRecurrenceText(appointment.series.rrule)}</PlainValue>
            ) : (
              <EmptyValue>One-time</EmptyValue>
            )
          }
          popoverWidth={240}
          editor={({ onClose }) => (
            <RecurrenceEditor
              value={recurrenceValue}
              isSaving={isSavingRecurrence}
              onChange={value => {
                void persistRecurrenceUpdate(value)
                  .then(onClose)
                  .catch((error: unknown) => handleUpdateFailure('recurrence', error));
              }}
            />
          )}
        />
      )}

      {showRow('forms') && (
        <EditablePropertyRow
          icon={AssignmentOutlined}
          label="Forms"
          disabled={!isRowInteractive('forms')}
          editorMode={editorMode}
          value={<ChipList items={forms} emptyLabel="No forms" />}
          popoverWidth={320}
          editor={() => (
            <FormsEditor
              value={forms}
              isSaving={isUpdatingAppointment}
              onChange={nextForms => {
                void persistAppointmentUpdate({
                  form_rules: toFormRules(nextForms),
                  reminder_rules: toReminderRules(reminders),
                  override_defaults: true,
                }).catch((error: unknown) => handleUpdateFailure('forms', error));
              }}
            />
          )}
        />
      )}

      {showRow('reminders') && (
        <EditablePropertyRow
          icon={NotificationsOutlined}
          label="Reminders"
          disabled={!isRowInteractive('reminders')}
          editorMode={editorMode}
          value={<ChipList items={reminders} emptyLabel="No reminders" />}
          popoverWidth={280}
          editor={() => (
            <RemindersEditor
              value={reminders}
              isSaving={isUpdatingAppointment}
              onChange={nextReminders => {
                void persistAppointmentUpdate({
                  form_rules: toFormRules(forms),
                  reminder_rules: toReminderRules(nextReminders),
                  override_defaults: true,
                }).catch((error: unknown) => handleUpdateFailure('reminders', error));
              }}
            />
          )}
        />
      )}

      {showRow('notes') && (
        <EditablePropertyRow
          icon={StickyNote2Outlined}
          label="Notes"
          disabled={!isRowInteractive('notes')}
          alignTop
          editorMode={editorMode}
          popoverWidth={340}
          value={
            appointment.notes ? (
              <Typography
                variant="body2"
                sx={{
                  fontSize: 13,
                  color: ELIG_TOKENS.ink,
                  fontStyle: 'italic',
                  lineHeight: 1.4,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: '8.4em',
                  overflowY: 'auto',
                }}
              >
                {appointment.notes}
              </Typography>
            ) : (
              <EmptyValue>Add a note...</EmptyValue>
            )
          }
          editor={() => (
            <NotesEditor
              value={appointment.notes ?? ''}
              isSaving={isUpdatingAppointment}
              onSave={notes => {
                return persistAppointmentUpdate({ notes }).catch((error: unknown) => {
                  handleUpdateFailure('notes', error);
                  throw error;
                });
              }}
            />
          )}
        />
      )}
      </Box>
    </AppointmentPropertyEditorInteractionContext.Provider>
  );
};

const StatusPropertyRow = ({
  appointment,
  editorMode,
}: {
  appointment: Appointment;
  editorMode: 'floating' | 'inline';
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const [optimisticStatus, setOptimisticStatus] = useState<DisplayAppointmentStatus | null>(null);
  const [transitionStatus, { isLoading }] = useTransitionAppointmentStatusMutation();
  const value = optimisticStatus ?? getDisplayAppointmentStatus(appointment);
  const display = getStatusDisplayForValue(value, appointment);

  useEffect(() => {
    setOptimisticStatus(null);
  }, [appointment.appointment_id, appointment.status, appointment.confirmation_status]);

  const commit = (nextStatus: DisplayAppointmentStatus, onClose: () => void) => {
    const previous = value;
    const transition = getStatusTransitionForDisplayStatus(appointment, nextStatus);

    setOptimisticStatus(nextStatus);
    dispatch(
      setOptimisticAppointmentPatch({
        appointmentId: appointment.appointment_id,
        patch: getOptimisticPatchForStatusTransition(appointment, transition),
      }),
    );
    onClose();

    void transitionStatus({
      appointmentId: appointment.appointment_id,
      appointmentStatusTransition: {
        ...transition,
        source: 'calendar_status_property',
        scope: 'single',
      },
    })
      .unwrap()
      .catch((error: unknown) => {
        setOptimisticStatus(previous);
        dispatch(clearOptimisticAppointmentPatch(appointment.appointment_id));
        console.error('Failed to update appointment status:', error);
        enqueueSnackbar('Failed to update status. Please try again.', { variant: 'error' });
      });
  };

  return (
    <EditablePropertyRow
      icon={CheckCircleOutlined}
      label="Status"
      editorMode={editorMode}
      popoverWidth={240}
      value={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <StatusValue color={display.color} label={display.label} />
          {isLoading && <CircularProgress size={12} />}
        </Box>
      }
      editor={({ onClose }) => (
        <StatusEditor
          value={value}
          isSaving={isLoading}
          onChange={nextStatus => commit(nextStatus, onClose)}
        />
      )}
    />
  );
};

const VisitStatusPropertyRow = ({
  appointment,
  editorMode,
}: {
  appointment: Appointment;
  editorMode: 'floating' | 'inline';
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const [optimisticStatus, setOptimisticStatus] = useState<VisitStatus | null>(null);
  const [updateVisitStatus, { isLoading }] = useUpdateAppointmentVisitStatusMutation();
  const value = optimisticStatus ?? coerceVisitStatus(appointment.visit_status);
  const display = getVisitStatusDisplay(appointment.no_showed ? 'no_show' : value);

  const commit = (status: VisitStatus, onClose: () => void) => {
    const previous = value;
    setOptimisticStatus(status);
    dispatch(
      setOptimisticAppointmentPatch({
        appointmentId: appointment.appointment_id,
        patch: {
          visit_status: status,
          visit_status_changed_at: new Date().toISOString(),
        } as Partial<Appointment>,
      }),
    );
    onClose();
    void updateVisitStatus({
      appointmentId: appointment.appointment_id,
      appointmentVisitStatusUpdate: { visit_status: status },
    })
      .unwrap()
      .catch((error: unknown) => {
        setOptimisticStatus(previous);
        dispatch(clearOptimisticAppointmentPatch(appointment.appointment_id));
        console.error('Failed to update appointment visit status:', error);
        enqueueSnackbar('Failed to update visit status. Please try again.', { variant: 'error' });
      });
  };

  return (
    <EditablePropertyRow
      icon={display.icon}
      label="Visit status"
      editorMode={editorMode}
      popoverWidth={220}
      value={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <DotValue color={display.color} label={display.label} />
          {isLoading && <CircularProgress size={12} />}
        </Box>
      }
      editor={({ onClose }) => (
        <VisitStatusEditor
          value={value}
          isSaving={isLoading}
          onChange={status => commit(status, onClose)}
        />
      )}
    />
  );
};

const VisitStatusEditor = ({
  value,
  isSaving,
  onChange,
}: {
  value: VisitStatus;
  isSaving: boolean;
  onChange: (value: VisitStatus) => void;
}) => (
  <EditorOptionList>
    {VISIT_STATUSES.map(status => {
      const option = getVisitStatusDisplay(status);
      return (
        <OptionRow
          key={status}
          selected={value === status}
          disabled={isSaving}
          onClick={() => onChange(status)}
        >
          <DotValue color={option.color} label={option.label} />
        </OptionRow>
      );
    })}
  </EditorOptionList>
);

const ProviderPropertyRow = ({
  appointment,
  providerName,
  clinicians,
  isLoadingClinicians,
  disabled,
  editorMode,
}: {
  appointment: Appointment;
  providerName: string | null;
  clinicians: Array<{ userId: string; name: string }>;
  isLoadingClinicians: boolean;
  disabled: boolean;
  editorMode: 'floating' | 'inline';
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const [optimisticProviderId, setOptimisticProviderId] = useState<string | null | undefined>();
  const [optimisticProviderName, setOptimisticProviderName] = useState<string | null | undefined>();
  const [reassignAppointment, { isLoading: isReassigning }] = useReassignAppointmentMutation();
  const { orgSchedule: orgScheduleTracking } = useEventTracking();
  const currentProviderId =
    optimisticProviderId !== undefined ? optimisticProviderId : appointment.provider_id ?? null;
  const currentProviderName =
    optimisticProviderName !== undefined ? optimisticProviderName : providerName;

  const commit = (providerId: string | null, onClose: () => void) => {
    const previousProviderId = currentProviderId;
    const previousProviderName = currentProviderName;
    const nextProviderName =
      providerId === null
        ? null
        : clinicians.find(clinician => clinician.userId === providerId)?.name ?? 'Assigned provider';

    setOptimisticProviderId(providerId);
    setOptimisticProviderName(nextProviderName);
    dispatch(
      setOptimisticAppointmentPatch({
        appointmentId: appointment.appointment_id,
        patch: {
          provider_id: providerId,
          provider: null,
        } as Partial<Appointment>,
      }),
    );
    onClose();

    void reassignAppointment({
      appointmentId: appointment.appointment_id,
      appointmentReassign: { provider_id: providerId },
    })
      .unwrap()
      .then(() => {
        orgScheduleTracking.providerReassigned({
          appointment_id: appointment.appointment_id,
          previous_provider_id: appointment.provider_id ?? null,
          new_provider_id: providerId,
        });
      })
      .catch((error: unknown) => {
        setOptimisticProviderId(previousProviderId);
        setOptimisticProviderName(previousProviderName);
        dispatch(clearOptimisticAppointmentPatch(appointment.appointment_id));
        console.error('Failed to update appointment provider:', error);
        enqueueSnackbar('Failed to update provider. Please try again.', { variant: 'error' });
      });
  };

  return (
    <EditablePropertyRow
      icon={PersonOutlined}
      label="Provider"
      disabled={disabled}
      editorMode={editorMode}
      popoverWidth={260}
      value={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {currentProviderName ? (
            <PlainValue>{currentProviderName}</PlainValue>
          ) : (
            <EmptyValue>Unassigned</EmptyValue>
          )}
          {isReassigning && <CircularProgress size={12} />}
        </Box>
      }
      editor={({ onClose }) => (
        <ProviderEditor
          value={currentProviderId}
          clinicians={clinicians}
          isLoading={isLoadingClinicians}
          isSaving={isReassigning}
          onChange={providerId => commit(providerId, onClose)}
        />
      )}
    />
  );
};

const PatientPropertyRow = ({
  appointment,
  disabled,
  editorMode,
  onUpdate,
}: {
  appointment: Appointment;
  disabled: boolean;
  editorMode: 'floating' | 'inline';
  onUpdate: (
    updates: AppointmentUpdate,
    optimisticAppointmentPatch?: Partial<Appointment>,
  ) => Promise<Appointment>;
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [optimisticPatient, setOptimisticPatient] = useState<Appointment['patient'] | undefined>();
  const currentPatient = optimisticPatient !== undefined ? optimisticPatient : appointment.patient;

  useEffect(() => {
    setOptimisticPatient(undefined);
  }, [appointment.appointment_id]);

  const commit = (patient: PatientRecord | undefined, onClose: () => void) => {
    const previousPatient = currentPatient;
    const nextPatient = mapPatientRecordToAppointmentPatient(patient);

    setOptimisticPatient(nextPatient);
    onClose();

    void onUpdate(
      { patient_id: patient?.patient_id ?? null },
      {
        patient: nextPatient,
      },
    ).catch((error: unknown) => {
      setOptimisticPatient(previousPatient);
      console.error('Failed to update appointment patient:', error);
      enqueueSnackbar('Failed to update patient. Please try again.', { variant: 'error' });
    });
  };

  return (
    <EditablePropertyRow
      icon={PersonOutlined}
      label="Patient"
      disabled={disabled}
      editorMode={editorMode}
      popoverWidth={320}
      value={
        currentPatient?.name ? (
          <PlainValue>{currentPatient.name}</PlainValue>
        ) : (
          <EmptyValue>Unassigned</EmptyValue>
        )
      }
      editor={({ onClose }) => (
        <Box sx={{ p: 1.5, width: 300 }}>
          <PatientPicker
            initialPatient={currentPatient ?? undefined}
            onPatientSelect={patient => commit(patient, onClose)}
            autoFocus
            openOnFocus
            size="small"
            disableAddNew
          />
        </Box>
      )}
    />
  );
};

const EditablePropertyRow = ({
  icon,
  label,
  value,
  editor,
  disabled = false,
  alignTop = false,
  editorMode = 'floating',
  popoverWidth = 240,
}: {
  icon: ComponentType<SvgIconProps>;
  label: string;
  value: React.ReactNode;
  editor: (props: { onClose: () => void }) => React.ReactNode;
  disabled?: boolean;
  alignTop?: boolean;
  editorMode?: 'floating' | 'inline';
  popoverWidth?: number;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const onEditorInteraction = useContext(AppointmentPropertyEditorInteractionContext);
  const { refs, floatingStyles, context } = usePropertyEditorFloating(isOpen, setIsOpen);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useDismiss(context),
    useRole(context, { role: 'dialog' }),
  ]);

  if (disabled) {
    return (
      <PropertyRow icon={icon} label={label} alignTop={alignTop}>
        {value}
      </PropertyRow>
    );
  }

  if (editorMode === 'inline') {
    return (
      <Box
        onClick={event => event.stopPropagation()}
        onMouseDown={event => event.stopPropagation()}
        onMouseUp={event => event.stopPropagation()}
      >
        <PropertyRow
          icon={icon}
          label={label}
          alignTop={alignTop}
          active={isOpen}
          onClick={() => {
            onEditorInteraction?.();
            setIsOpen(open => !open);
          }}
        >
          {value}
        </PropertyRow>
        {isOpen && (
          <Box
            sx={{
              mt: 0.75,
              mb: 1,
              borderRadius: 1,
              border: `1px solid ${ELIG_TOKENS.lineStrong}`,
              bgcolor: ELIG_TOKENS.card,
              overflow: 'hidden',
            }}
          >
            {editor({ onClose: () => setIsOpen(false) })}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <>
      <PropertyRow
        icon={icon}
        label={label}
        alignTop={alignTop}
        active={isOpen}
        onClick={() => {
          onEditorInteraction?.();
          setIsOpen(open => !open);
        }}
        referenceRef={refs.setReference}
        interactionProps={getReferenceProps()}
      >
        {value}
      </PropertyRow>
      {isOpen && (
        <FloatingPortal>
          <FloatingEditorSurface
            width={popoverWidth}
            floatingRef={refs.setFloating}
            floatingStyles={floatingStyles}
            floatingProps={getFloatingProps()}
          >
            {editor({ onClose: () => setIsOpen(false) })}
          </FloatingEditorSurface>
        </FloatingPortal>
      )}
    </>
  );
};

const usePropertyEditorFloating = (isOpen: boolean, setIsOpen: (open: boolean) => void) =>
  useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

const FloatingEditorSurface = ({
  width,
  floatingRef,
  floatingStyles,
  floatingProps,
  children,
}: {
  width: number;
  floatingRef: (node: HTMLElement | null) => void;
  floatingStyles: React.CSSProperties;
  floatingProps: React.HTMLAttributes<HTMLElement>;
  children: React.ReactNode;
}) => (
  <Box
    {...floatingProps}
    data-appointment-property-editor-surface="true"
    ref={floatingRef}
    style={{ ...floatingStyles, zIndex: 1500 }}
    sx={{
      width,
      borderRadius: 2,
      border: `1px solid ${ELIG_TOKENS.lineStrong}`,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      bgcolor: ELIG_TOKENS.card,
      overflow: 'hidden',
    }}
  >
    {children}
  </Box>
);

const EditorOptionList = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      maxHeight: `min(${PROPERTY_EDITOR_OPTION_LIST_MAX_HEIGHT}px, calc(100vh - 180px))`,
      overflowY: 'auto',
      overscrollBehavior: 'contain',
      py: 0.5,
      scrollbarWidth: 'thin',
    }}
  >
    {children}
  </Box>
);

const AppointmentTypeEditor = ({
  value,
  providerId,
  isSaving,
  onChange,
}: {
  value: string | null;
  providerId?: string;
  isSaving: boolean;
  onChange: (updates: AppointmentUpdate, appointmentType: AppointmentType) => void;
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { appointmentTypes, isLoading } = useAppointmentTypes(searchTerm, providerId);

  return (
    <Box sx={{ py: 0.75 }}>
      <Box sx={{ px: 1, pb: 0.75 }}>
        <InputBase
          autoFocus
          fullWidth
          placeholder="Search types"
          value={searchTerm}
          onChange={event => setSearchTerm(event.target.value)}
          sx={{
            px: 1,
            py: 0.5,
            borderRadius: 1,
            border: `1px solid ${ELIG_TOKENS.line}`,
            fontSize: 13,
          }}
        />
      </Box>
      <EditorOptionList>
        {isLoading ? (
          <EditorLoading />
        ) : appointmentTypes.length === 0 ? (
          <Box sx={{ px: 1.5, py: 1 }}>
            <EmptyValue>No appointment types</EmptyValue>
          </Box>
        ) : (
          appointmentTypes.map(appointmentType => {
            const isSelected = value === appointmentType.encounter_type_id;

            return (
              <OptionRow
                key={appointmentType.encounter_type_id}
                selected={isSelected}
                disabled={isSaving || isSelected}
                onClick={() => {
                  if (isSelected) return;

                  onChange(
                    {
                      ...(appointmentType.encounter_type_id
                        ? { encounter_type_id: appointmentType.encounter_type_id }
                        : { encounter_type: appointmentType.encounter_type }),
                      ...(appointmentType.default_duration
                        ? { duration: appointmentType.default_duration }
                        : {}),
                      ...(appointmentType.telehealth != null
                        ? { telehealth: appointmentType.telehealth }
                        : {}),
                    },
                    appointmentType,
                  );
                }}
              >
                <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                  <PlainValue>{appointmentType.encounter_type}</PlainValue>
                  {appointmentType.default_duration ? (
                    <Typography variant="caption" sx={{ fontSize: 11, color: ELIG_TOKENS.inkSoft }}>
                      {appointmentType.default_duration} min
                    </Typography>
                  ) : null}
                </Stack>
              </OptionRow>
            );
          })
        )}
      </EditorOptionList>
    </Box>
  );
};

const ProviderEditor = ({
  value,
  clinicians,
  isLoading,
  isSaving,
  onChange,
}: {
  value: string | null;
  clinicians: Array<{ userId: string; name: string }>;
  isLoading: boolean;
  isSaving: boolean;
  onChange: (providerId: string | null) => void;
}) => (
  <EditorOptionList>
    {isLoading ? (
      <EditorLoading />
    ) : (
      <>
        {clinicians.map(clinician => (
          <OptionRow
            key={clinician.userId}
            selected={value === clinician.userId}
            disabled={isSaving}
            onClick={() => onChange(clinician.userId)}
          >
            <PlainValue>{clinician.name}</PlainValue>
          </OptionRow>
        ))}
        <OptionRow
          selected={value === null}
          disabled={isSaving}
          onClick={() => onChange(null)}
        >
          <EmptyValue>Unassigned</EmptyValue>
        </OptionRow>
      </>
    )}
  </EditorOptionList>
);

const STATUS_OPTIONS: DisplayAppointmentStatus[] = [
  'scheduled',
  'confirmed',
  'cancelled',
  'reschedule_requested',
];

const StatusEditor = ({
  value,
  isSaving,
  onChange,
}: {
  value: DisplayAppointmentStatus;
  isSaving: boolean;
  onChange: (value: DisplayAppointmentStatus) => void;
}) => (
  <EditorOptionList>
    {STATUS_OPTIONS.map(status => {
      const option = getStatusDisplayForValue(status);
      return (
        <OptionRow
          key={status}
          selected={status === value}
          disabled={isSaving}
          onClick={() => onChange(status)}
        >
          <StatusValue color={option.color} label={option.label} />
        </OptionRow>
      );
    })}
  </EditorOptionList>
);

const ChoiceEditor = <T extends string | boolean>({
  value,
  options,
  isSaving,
  onChange,
}: {
  value: T;
  options: Array<{ label: string; value: T }>;
  isSaving: boolean;
  onChange: (value: T) => void;
}) => (
  <EditorOptionList>
    {options.map(option => (
      <OptionRow
        key={String(option.value)}
        selected={option.value === value}
        disabled={isSaving}
        onClick={() => onChange(option.value)}
      >
        <PlainValue>{option.label}</PlainValue>
      </OptionRow>
    ))}
  </EditorOptionList>
);

const TimeEditor = ({
  value,
  isSaving,
  onClose,
  onChange,
}: {
  value: string;
  isSaving: boolean;
  onClose: () => void;
  onChange: (value: string) => void;
}) => {
  const initialDraft = toDateTimeDraft(value);
  const [draftDate, setDraftDate] = useState(initialDraft.date);
  const [draftTime, setDraftTime] = useState(initialDraft.time);

  useEffect(() => {
    const nextDraft = toDateTimeDraft(value);
    setDraftDate(nextDraft.date);
    setDraftTime(nextDraft.time);
  }, [value]);

  const committedDraft = toDateTimeDraft(value);
  const hasChanged = draftDate !== committedDraft.date || draftTime !== committedDraft.time;
  const canApply =
    isDateDraftValid(draftDate) && isTimeDraftValid(draftTime) && hasChanged && !isSaving;

  const commit = () => {
    if (!canApply) return;

    onChange(new Date(`${draftDate}T${draftTime}`).toISOString());
    onClose();
  };

  return (
    <Stack spacing={1.25} sx={{ p: 1.5 }} onClick={event => event.stopPropagation()}>
      <Stack direction="row" spacing={1}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Date"
          placeholder="YYYY-MM-DD"
          value={draftDate}
          disabled={isSaving}
          onChange={event => setDraftDate(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') commit();
          }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          fullWidth
          size="small"
          label="Time"
          placeholder="HH:MM"
          value={draftTime}
          disabled={isSaving}
          onChange={event => setDraftTime(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') commit();
          }}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>
      <Button
        size="small"
        variant="contained"
        disabled={!canApply}
        onClick={commit}
        sx={{ alignSelf: 'flex-end', textTransform: 'none' }}
      >
        Apply
      </Button>
    </Stack>
  );
};

const DurationEditor = ({
  value,
  startTime,
  isSaving,
  onChange,
  onClose,
}: {
  value: number;
  startTime: string;
  isSaving: boolean;
  onChange: (value: number) => void;
  onClose: () => void;
}) => {
  const startMs = getValidDateMs(startTime);
  const isCustom = !COMMON_DURATIONS.includes(value as (typeof COMMON_DURATIONS)[number]);
  const [customDraft, setCustomDraft] = useState(isCustom ? String(value) : '');
  const [endDraft, setEndDraft] = useState(() =>
    formatTimeForInput(new Date(startMs + value * 60_000)),
  );

  useEffect(() => {
    setCustomDraft(isCustom ? String(value) : '');
    setEndDraft(formatTimeForInput(new Date(startMs + value * 60_000)));
  }, [isCustom, startMs, value]);

  const commitCustom = () => {
    const duration = parseInt(customDraft, 10);
    if (Number.isFinite(duration) && duration > 0 && duration !== value) {
      onChange(duration);
    }
  };

  const commitEnd = () => {
    const duration = durationFromEndTime(endDraft, startMs);
    if (duration != null && duration > 0 && duration !== value) {
      onChange(duration);
    }
  };

  return (
    <EditorOptionList>
      {COMMON_DURATIONS.map(duration => (
        <OptionRow
          key={duration}
          selected={value === duration}
          disabled={isSaving}
          onClick={() => {
            onChange(duration);
            onClose();
          }}
        >
          <Typography variant="body2" sx={{ flex: 1, fontSize: 13, color: ELIG_TOKENS.ink }}>
            {duration} min
          </Typography>
          <Typography variant="caption" sx={{ fontSize: 11, color: ELIG_TOKENS.inkFaint }}>
            until {formatTimeOfDay(new Date(startMs + duration * 60_000))}
          </Typography>
        </OptionRow>
      ))}

      <Box sx={{ height: 1, mx: 1, my: 0.5, bgcolor: ELIG_TOKENS.line }} />

      <DurationInputRow
        label="Custom"
        suffix="min"
        type="number"
        placeholder="50"
        value={customDraft}
        selected={isCustom}
        disabled={isSaving}
        inputWidth={56}
        onChange={setCustomDraft}
        onCommit={commitCustom}
      />

      <DurationInputRow
        label="End at"
        type="text"
        placeholder="HH:MM"
        value={endDraft}
        selected={false}
        disabled={isSaving}
        inputWidth={92}
        onChange={setEndDraft}
        onCommit={commitEnd}
      />
    </EditorOptionList>
  );
};

const DurationInputRow = ({
  label,
  value,
  selected,
  disabled,
  type,
  suffix,
  placeholder,
  inputWidth,
  onChange,
  onCommit,
}: {
  label: string;
  value: string;
  selected: boolean;
  disabled: boolean;
  type: 'number' | 'time' | 'text';
  suffix?: string;
  placeholder?: string;
  inputWidth: number;
  onChange: (value: string) => void;
  onCommit: () => void;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 1.5,
      py: 0.75,
      bgcolor: selected ? 'rgba(28, 30, 133, 0.05)' : 'transparent',
    }}
  >
    <Typography
      variant="body2"
      sx={{
        flex: 1,
        fontSize: 13,
        color: selected ? ELIG_TOKENS.midnight : ELIG_TOKENS.inkSoft,
        fontWeight: selected ? 600 : 400,
      }}
    >
      {label}
    </Typography>
    <InputBase
      type={type}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      onChange={event => onChange(event.target.value)}
      onBlur={onCommit}
      onKeyDown={event => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.currentTarget.blur();
        }
      }}
      onClick={event => event.stopPropagation()}
      sx={{
        width: inputWidth,
        fontSize: 13,
        color: ELIG_TOKENS.ink,
        border: `1px solid ${ELIG_TOKENS.line}`,
        borderRadius: 1,
        px: 0.75,
        py: 0.25,
        '&.Mui-focused': { borderColor: ELIG_TOKENS.midnight },
        '&.Mui-disabled': {
          color: ELIG_TOKENS.inkFaint,
          bgcolor: 'rgba(0,0,0,0.025)',
        },
        '& input[type=number]::-webkit-inner-spin-button': { display: 'none' },
        '& input[type=number]::-webkit-outer-spin-button': { display: 'none' },
        '& input[type=number]': { MozAppearance: 'textfield' },
      }}
    />
    {suffix && (
      <Typography variant="caption" sx={{ fontSize: 11, color: ELIG_TOKENS.inkFaint }}>
        {suffix}
      </Typography>
    )}
  </Box>
);

const formatTimeOfDay = (date: Date): string => {
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const meridiem = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12;
  return `${hours}:${String(minutes).padStart(2, '0')} ${meridiem}`;
};

const formatTimeForInput = (date: Date): string => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const durationFromEndTime = (time: string, startMs: number): number | null => {
  const [hoursPart, minutesPart] = time.split(':');
  const hours = parseInt(hoursPart, 10);
  const minutes = parseInt(minutesPart, 10);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;

  const end = new Date(startMs);
  end.setHours(hours, minutes, 0, 0);
  let duration = Math.round((end.getTime() - startMs) / 60_000);
  if (duration <= 0) duration += 24 * 60;
  return duration;
};

const getValidDateMs = (value: string): number => {
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : Date.now();
};

const RecurrenceEditor = ({
  value,
  isSaving,
  onChange,
}: {
  value: RecurrencePresetValue;
  isSaving: boolean;
  onChange: (value: RecurrencePresetValue) => void;
}) => (
  <EditorOptionList>
    {RECURRENCE_PRESETS.map(preset => (
      <OptionRow
        key={preset.value}
        selected={value === preset.value}
        disabled={isSaving}
        onClick={() => onChange(preset.value)}
      >
        <PlainValue>{preset.label}</PlainValue>
      </OptionRow>
    ))}
    {value === 'custom' && (
      <>
        <Box sx={{ height: 1, mx: 1, my: 0.5, bgcolor: ELIG_TOKENS.line }} />
        <OptionRow selected disabled onClick={() => undefined}>
          <EmptyValue>Custom recurrence</EmptyValue>
        </OptionRow>
      </>
    )}
  </EditorOptionList>
);

const FormsEditor = ({
  value,
  isSaving,
  onChange,
}: {
  value: AttachedRuleItem[];
  isSaving: boolean;
  onChange: (items: AttachedRuleItem[]) => void;
}) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 250);
  const { forms, isLoading } = useForms({
    search: debouncedSearch || undefined,
    pageSize: 50,
  });
  const selectedIds = new Set(value.map(item => item.formDefinitionId).filter(Boolean));
  const availableForms = forms
    .map(formEntryToAttached)
    .filter((item): item is AttachedRuleItem => Boolean(item?.formDefinitionId))
    .filter(item => !selectedIds.has(item.formDefinitionId));

  const commit = (items: AttachedRuleItem[]) => onChange(items);
  const addItem = (item: AttachedRuleItem) =>
    commit([
      ...value,
      {
        ...item,
        offset: DEFAULT_FORM_OFFSET,
        timing: formatRuleTiming(DEFAULT_FORM_OFFSET),
      },
    ]);
  const removeItem = (id: string) => commit(value.filter(item => item.id !== id));
  const updateOffset = (id: string, offset: string) =>
    commit(
      value.map(item =>
        item.id === id ? { ...item, offset, timing: formatRuleTiming(offset) } : item,
      ),
    );

  return (
    <Box>
      {value.length > 0 && (
        <Box sx={{ borderBottom: `1px solid ${ELIG_TOKENS.line}`, py: 0.5 }}>
          {value.map(item => (
            <SelectedRuleRow
              key={item.id}
              item={item}
              presets={FORM_TIMING_PRESETS}
              disabled={isSaving}
              onChangeOffset={offset => updateOffset(item.id, offset)}
              onRemove={() => removeItem(item.id)}
            />
          ))}
        </Box>
      )}
      <Box sx={{ p: 1, borderBottom: `1px solid ${ELIG_TOKENS.line}` }}>
        <InputBase
          autoFocus
          fullWidth
          placeholder="Search forms..."
          value={search}
          disabled={isSaving}
          onChange={event => setSearch(event.target.value)}
          onClick={event => event.stopPropagation()}
          sx={{ px: 1, py: 0.5, fontSize: 13, color: ELIG_TOKENS.ink }}
        />
      </Box>
      <EditorOptionList>
        {isLoading && forms.length === 0 && <EditorHint>Loading forms...</EditorHint>}
        {!isLoading && availableForms.length === 0 && (
          <EditorHint>{search ? 'No forms match' : 'No forms available'}</EditorHint>
        )}
        {availableForms.map(item => (
          <OptionRow
            key={item.id}
            selected={false}
            disabled={isSaving}
            onClick={() => addItem(item)}
          >
            <RuleCheckbox checked={false} />
            <PlainValue>{item.title}</PlainValue>
          </OptionRow>
        ))}
      </EditorOptionList>
    </Box>
  );
};

const RemindersEditor = ({
  value,
  isSaving,
  onChange,
}: {
  value: AttachedRuleItem[];
  isSaving: boolean;
  onChange: (items: AttachedRuleItem[]) => void;
}) => {
  const selectedIds = new Set(value.map(item => item.reminderTemplateId).filter(Boolean));
  const canAddReminder = !selectedIds.has(APPOINTMENT_REMINDER.reminderTemplateId);

  const commit = (items: AttachedRuleItem[]) => onChange(items);
  const addReminder = () =>
    commit([
      ...value,
      {
        id: APPOINTMENT_REMINDER.id,
        title: APPOINTMENT_REMINDER.title,
        reminderTemplateId: APPOINTMENT_REMINDER.reminderTemplateId,
        offset: DEFAULT_REMINDER_OFFSET,
        timing: formatRuleTiming(DEFAULT_REMINDER_OFFSET),
      },
    ]);
  const removeItem = (id: string) => commit(value.filter(item => item.id !== id));
  const updateOffset = (id: string, offset: string) =>
    commit(
      value.map(item =>
        item.id === id ? { ...item, offset, timing: formatRuleTiming(offset) } : item,
      ),
    );

  return (
    <Box>
      {value.length > 0 && (
        <Box sx={{ borderBottom: `1px solid ${ELIG_TOKENS.line}`, py: 0.5 }}>
          {value.map(item => (
            <SelectedRuleRow
              key={item.id}
              item={item}
              presets={REMINDER_TIMING_PRESETS}
              disabled={isSaving}
              onChangeOffset={offset => updateOffset(item.id, offset)}
              onRemove={() => removeItem(item.id)}
            />
          ))}
        </Box>
      )}
      <EditorOptionList>
        {canAddReminder ? (
          <OptionRow selected={false} disabled={isSaving} onClick={addReminder}>
            <RuleCheckbox checked={false} />
            <PlainValue>{APPOINTMENT_REMINDER.title}</PlainValue>
          </OptionRow>
        ) : (
          <EditorHint>No reminders available</EditorHint>
        )}
      </EditorOptionList>
    </Box>
  );
};

const SelectedRuleRow = ({
  item,
  presets,
  disabled,
  onChangeOffset,
  onRemove,
}: {
  item: AttachedRuleItem;
  presets: readonly TimingPreset[];
  disabled: boolean;
  onChangeOffset: (offset: string) => void;
  onRemove: () => void;
}) => {
  const [timingOpen, setTimingOpen] = useState(false);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          bgcolor: 'rgba(28, 30, 133, 0.04)',
        }}
      >
        <ButtonBase
          disabled={disabled}
          onClick={event => {
            event.stopPropagation();
            onRemove();
          }}
          sx={{ borderRadius: '3px' }}
        >
          <RuleCheckbox checked />
        </ButtonBase>
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 13,
            color: ELIG_TOKENS.ink,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.title}
        </Typography>
        <ButtonBase
          disabled={disabled}
          onClick={event => {
            event.stopPropagation();
            setTimingOpen(open => !open);
          }}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.25,
            px: 0.5,
            py: 0.25,
            borderRadius: 0.5,
            color: ELIG_TOKENS.inkSoft,
            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
          }}
        >
          <Typography variant="caption" sx={{ fontSize: 11 }}>
            {item.timing || 'Set timing'}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: 10 }}>
            v
          </Typography>
        </ButtonBase>
      </Box>
      {timingOpen && (
        <Box sx={{ pl: 4, py: 0.25, bgcolor: 'rgba(28, 30, 133, 0.02)' }}>
          {presets.map(preset => (
            <OptionRow
              key={preset.iso}
              selected={item.offset === preset.iso}
              disabled={disabled}
              onClick={() => {
                onChangeOffset(preset.iso);
                setTimingOpen(false);
              }}
            >
              <Typography variant="body2" sx={{ flex: 1, fontSize: 12, color: ELIG_TOKENS.ink }}>
                {preset.display}
              </Typography>
            </OptionRow>
          ))}
        </Box>
      )}
    </Box>
  );
};

const RuleCheckbox = ({ checked }: { checked: boolean }) => (
  <Box
    sx={{
      width: 14,
      height: 14,
      borderRadius: '3px',
      border: `1.5px solid ${checked ? ELIG_TOKENS.midnight : ELIG_TOKENS.lineStrong}`,
      bgcolor: checked ? ELIG_TOKENS.midnight : 'transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    {checked && <Box sx={{ width: 6, height: 6, borderRadius: '1px', bgcolor: '#fff' }} />}
  </Box>
);

const EditorHint = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ px: 1.5, py: 1 }}>
    <Typography variant="caption" sx={{ fontSize: 12, color: ELIG_TOKENS.inkFaint }}>
      {children}
    </Typography>
  </Box>
);

const NotesEditor = ({
  value,
  isSaving,
  onSave,
}: {
  value: string;
  isSaving: boolean;
  onSave: (value: string) => Promise<unknown>;
}) => {
  const [draft, setDraft] = useState(value);
  const debouncedDraft = useDebounce(draft, 650);
  const lastCommittedRef = useRef(value);
  const onEditorInteraction = useContext(AppointmentPropertyEditorInteractionContext);

  useEffect(() => {
    setDraft(value);
    lastCommittedRef.current = value;
  }, [value]);

  useEffect(() => {
    if (debouncedDraft === lastCommittedRef.current) return;

    lastCommittedRef.current = debouncedDraft;
    void onSave(debouncedDraft).catch(() => {
      lastCommittedRef.current = value;
    });
  }, [debouncedDraft, onSave, value]);

  return (
    <Stack spacing={0.75} sx={{ p: 1.25 }}>
      <TextField
        autoFocus
        fullWidth
        multiline
        minRows={4}
        maxRows={8}
        size="small"
        placeholder="Add notes"
        value={draft}
        onFocus={onEditorInteraction ?? undefined}
        onChange={event => {
          onEditorInteraction?.();
          setDraft(event.target.value);
        }}
      />
      <Typography
        variant="caption"
        sx={{
          minHeight: 16,
          fontSize: 11,
          color: isSaving ? ELIG_TOKENS.inkFaint : 'transparent',
        }}
      >
        Saving...
      </Typography>
    </Stack>
  );
};

const EditorFrame = ({
  children,
  isSaving,
  canSave,
  onSave,
}: {
  children: React.ReactNode;
  isSaving: boolean;
  canSave: boolean;
  onSave: () => void;
}) => {
  const onEditorInteraction = useContext(AppointmentPropertyEditorInteractionContext);

  return (
    <Stack spacing={1.25} sx={{ p: 1.25 }}>
      {children}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          size="small"
          variant="contained"
          disabled={isSaving || !canSave}
          onClick={() => {
            onEditorInteraction?.();
            onSave();
          }}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </Box>
    </Stack>
  );
};

const EditorLoading = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1 }}>
    <CircularProgress size={14} />
    <EmptyValue>Loading...</EmptyValue>
  </Box>
);

const PropertyRow = ({
  icon: Icon,
  label,
  children,
  alignTop = false,
  active = false,
  onClick,
  referenceRef,
  interactionProps,
}: {
  icon: ComponentType<SvgIconProps>;
  label: string;
  children: React.ReactNode;
  alignTop?: boolean;
  active?: boolean;
  onClick?: React.MouseEventHandler<HTMLElement>;
  referenceRef?: (node: HTMLElement | null) => void;
  interactionProps?: React.HTMLAttributes<HTMLElement>;
}) => {
  const content = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: ELIG_TOKENS.inkSoft }}>
        <Icon sx={{ fontSize: 14, mt: alignTop ? 0.25 : 0 }} />
        <Typography
          variant="body2"
          sx={{ fontSize: 12, fontWeight: 500, color: ELIG_TOKENS.inkSoft }}
        >
          {label}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0 }}>{children}</Box>
    </>
  );

  const sx = {
    display: 'grid',
    gridTemplateColumns: '120px minmax(0, 1fr)',
    alignItems: alignTop ? 'flex-start' : 'center',
    gap: 1,
    py: 0.75,
    px: 0.5,
    borderRadius: 1,
    transition: 'background-color 0.12s',
    ...(onClick
      ? {
          cursor: 'pointer',
          ...(active
            ? { bgcolor: 'rgba(0,0,0,0.04)' }
            : { '&:hover': { bgcolor: 'rgba(0,0,0,0.025)' } }),
        }
      : {}),
  };

  if (!onClick) return <Box sx={sx}>{content}</Box>;

  return (
    <Box
      {...interactionProps}
      ref={referenceRef}
      component="button"
      type="button"
      onClick={onClick}
      sx={{ ...sx, border: 0, bgcolor: 'transparent', width: '100%', textAlign: 'left' }}
    >
      {content}
    </Box>
  );
};

const OptionRow = ({
  selected,
  disabled = false,
  onClick,
  children,
}: {
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => {
  const committedOnPointerDownRef = useRef(false);
  const onEditorInteraction = useContext(AppointmentPropertyEditorInteractionContext);

  const commit = () => {
    if (disabled) return;
    onEditorInteraction?.();
    onClick();
  };

  const commitFromPress = (event: React.MouseEvent | React.PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (committedOnPointerDownRef.current) return;
    committedOnPointerDownRef.current = true;
    commit();
  };

  return (
    <ButtonBase
      disabled={disabled}
      onPointerDown={commitFromPress}
      onMouseDown={commitFromPress}
      onClick={event => {
        event.stopPropagation();
        if (committedOnPointerDownRef.current) {
          committedOnPointerDownRef.current = false;
          return;
        }
        commit();
      }}
      sx={{
        width: '100%',
        justifyContent: 'flex-start',
        gap: 1,
        px: 1.5,
        py: 0.75,
        color: ELIG_TOKENS.ink,
        bgcolor: selected ? 'rgba(28, 30, 133, 0.06)' : 'transparent',
        '&:hover': { bgcolor: selected ? 'rgba(28, 30, 133, 0.08)' : 'rgba(0,0,0,0.035)' },
        '&.Mui-disabled': {
          color: ELIG_TOKENS.inkFaint,
        },
      }}
    >
      {children}
    </ButtonBase>
  );
};

const DotValue = ({ color, label }: { color: string; label: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: color,
        flexShrink: 0,
      }}
    />
    <PlainValue>{label}</PlainValue>
  </Box>
);

const StatusValue = ({ color, label }: { color: string; label: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
    <PlainValue>{label}</PlainValue>
  </Box>
);

const PlainValue = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="body2"
    sx={{
      fontSize: 13,
      color: ELIG_TOKENS.ink,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Typography>
);

const EmptyValue = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body2" sx={{ fontSize: 13, color: ELIG_TOKENS.inkFaint }}>
    {children}
  </Typography>
);

const ChipList = ({ items, emptyLabel }: { items: AttachedRuleItem[]; emptyLabel: string }) => {
  if (items.length === 0) return <EmptyValue>{emptyLabel}</EmptyValue>;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {items.map(item => (
        <Box
          key={item.id}
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 0.5,
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            bgcolor: 'rgba(28, 30, 133, 0.05)',
            border: `1px solid ${ELIG_TOKENS.line}`,
            lineHeight: 1.4,
          }}
        >
          <Typography variant="caption" sx={{ fontSize: 11, color: ELIG_TOKENS.ink }}>
            {item.title}
          </Typography>
          {item.timing ? (
            <Typography variant="caption" sx={{ fontSize: 10, color: ELIG_TOKENS.inkFaint }}>
              {item.timing}
            </Typography>
          ) : null}
        </Box>
      ))}
    </Box>
  );
};

const getAppointmentProviderName = (appointment: Appointment): string | null => {
  const provider = appointment.provider as { name?: unknown } | null | undefined;
  return typeof provider?.name === 'string' && provider.name ? provider.name : null;
};

const lookupProviderName = (
  providerId: string | null | undefined,
  clinicians: Array<{ userId: string; name: string }>,
): string | null => {
  if (!providerId) return null;
  return clinicians.find(clinician => clinician.userId === providerId)?.name ?? 'Assigned provider';
};

const mapFormRules = (rules: Appointment['form_rules']): AttachedRuleItem[] =>
  rules
    ?.map((rule, index): AttachedRuleItem | null => {
      const formDefinitionId = getRuleString(rule, 'form_definition_id');
      const ruleId = getRuleString(rule, 'id');
      const offset = getRuleString(rule, 'offset') ?? '';
      const title = getRuleString(rule, 'title') ?? formDefinitionId ?? `Form ${index + 1}`;
      if (!formDefinitionId && !title) return null;
      return {
        id: ruleId ?? formDefinitionId ?? `form-${index}`,
        title,
        offset,
        timing: formatRuleTiming(offset),
        formDefinitionId: formDefinitionId ?? undefined,
        ruleId,
      };
    })
    .filter((item): item is AttachedRuleItem => item !== null) ?? [];

const mapReminderRules = (rules: Appointment['reminder_rules']): AttachedRuleItem[] =>
  rules
    ?.map((rule, index): AttachedRuleItem | null => {
      const reminderTemplateId = getRuleString(rule, 'reminder_template_id');
      const ruleId = getRuleString(rule, 'id');
      const offset = getRuleString(rule, 'offset') ?? '';
      const title = getRuleString(rule, 'title') ?? reminderTemplateId ?? `Reminder ${index + 1}`;
      if (!reminderTemplateId && !title) return null;
      return {
        id: ruleId ?? reminderTemplateId ?? `reminder-${index}`,
        title,
        offset,
        timing: formatRuleTiming(offset),
        reminderTemplateId: reminderTemplateId ?? undefined,
        ruleId,
      };
    })
    .filter((item): item is AttachedRuleItem => item !== null) ?? [];

const toFormRules = (items: AttachedRuleItem[]): AppointmentFormRule[] =>
  items
    .filter(item => item.formDefinitionId)
    .map(item => ({
      form_definition_id: item.formDefinitionId ?? '',
      offset: item.offset,
      title: item.title,
      ...(item.ruleId ? { id: item.ruleId } : {}),
    }));

const toReminderRules = (items: AttachedRuleItem[]): AppointmentReminderRule[] =>
  items
    .filter(item => item.reminderTemplateId)
    .map(item => ({
      reminder_template_id: item.reminderTemplateId ?? '',
      offset: item.offset,
      title: item.title,
      ...(item.ruleId ? { id: item.ruleId } : {}),
    }));

const formEntryToAttached = (entry: FormWithRevisionSummary): AttachedRuleItem | null => {
  const formDefinitionId = entry.form.id;
  const title = entry.current_revision?.title ?? null;
  if (!formDefinitionId || !title) return null;
  return {
    id: formDefinitionId,
    title,
    formDefinitionId,
    offset: DEFAULT_FORM_OFFSET,
    timing: formatRuleTiming(DEFAULT_FORM_OFFSET),
  };
};

const getRuleString = (rule: unknown, key: string): string | null => {
  if (!rule || typeof rule !== 'object' || !(key in rule)) return null;
  const value = (rule as Record<string, unknown>)[key];
  return typeof value === 'string' && value ? value : null;
};

const formatRuleTiming = (offset: string): string => {
  if (!offset) return '';
  try {
    return formatAppointmentDuration(offset);
  } catch {
    return offset;
  }
};

const getRecurrencePresetValue = (rrule: string | null | undefined): RecurrencePresetValue => {
  if (!rrule) return 'none';
  try {
    const rule = rrulestr(rrule);
    if (!(rule instanceof RRule)) return 'custom';

    if (rule.options.freq === RRule.DAILY) return 'daily';
    if (rule.options.freq === RRule.WEEKLY) {
      return rule.options.interval === 2 ? 'biweekly' : 'weekly';
    }
    if (rule.options.freq === RRule.MONTHLY) return 'monthly';
    if (rule.options.freq === RRule.YEARLY) return 'yearly';
    return 'custom';
  } catch {
    return 'custom';
  }
};

const getRRuleForRecurrencePreset = (value: RecurrencePresetValue): string | null => {
  switch (value) {
    case 'daily':
      return new RRule({ freq: RRule.DAILY }).toString();
    case 'weekly':
      return new RRule({ freq: RRule.WEEKLY }).toString();
    case 'biweekly':
      return new RRule({ freq: RRule.WEEKLY, interval: 2 }).toString();
    case 'monthly':
      return new RRule({ freq: RRule.MONTHLY }).toString();
    case 'yearly':
      return new RRule({ freq: RRule.YEARLY }).toString();
    case 'none':
    case 'custom':
      return null;
  }
};

const coerceVisitStatus = (status: string | null | undefined): VisitStatus =>
  status && VISIT_STATUSES.includes(status as VisitStatus)
    ? (status as VisitStatus)
    : 'not_started';

const toDateTimeLocalValue = (value: string): string => {
  try {
    return format(parseISO(value), "yyyy-MM-dd'T'HH:mm");
  } catch {
    return '';
  }
};

const toDateTimeDraft = (value: string): { date: string; time: string } => {
  const localValue = toDateTimeLocalValue(value);
  const [date = '', time = ''] = localValue.split('T');
  return { date, time };
};

const isDateDraftValid = (value: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(value);

const isTimeDraftValid = (value: string): boolean => /^([01]\d|2[0-3]):[0-5]\d$/.test(value);

const mapPatientRecordToAppointmentPatient = (
  patient: PatientRecord | undefined,
): Appointment['patient'] | null => {
  if (!patient) return null;

  const name = [patient.first_name, patient.middle_name, patient.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

  return {
    dob: patient.dob ?? null,
    first_name: patient.first_name ?? null,
    identifier: patient.identifier ?? null,
    last_name: patient.last_name ?? null,
    middle_name: patient.middle_name ?? null,
    name: name || null,
    patient_id: patient.patient_id,
    preferred_name: patient.preferred_name ?? null,
    telephone: patient.mobile_phone ?? patient.phone ?? null,
  };
};

const getStatusDisplay = (appointment: Appointment): { label: string; color: string } => {
  return getStatusDisplayForValue(getDisplayAppointmentStatus(appointment), appointment);
};

const getDisplayAppointmentStatus = (appointment: Appointment): DisplayAppointmentStatus => {
  const reality = appointment.block_state?.reality;
  if (appointment.status === 'cancelled' || reality === 'cancelled') return 'cancelled';
  if (appointment.confirmation_status === 'cancelled') {
    return 'cancelled';
  }
  if (
    appointment.confirmation_status === 'reschedule_requested' ||
    reality === 'rescheduling'
  ) {
    return 'reschedule_requested';
  }
  if (appointment.confirmation_status === 'confirmed') return 'confirmed';
  return 'scheduled';
};

const getStatusDisplayForValue = (
  status: DisplayAppointmentStatus,
  appointment?: Appointment,
): { label: string; color: string } => {
  if (appointment?.block_state?.reality === 'suggested' || appointment?.status === 'suggested') {
    return { label: 'Suggested', color: ELIG_TOKENS.midnight };
  }
  if (appointment?.block_state?.reality === 'tentative') {
    return { label: 'Tentative', color: ELIG_TOKENS.gray };
  }
  if (appointment?.block_state?.reality === 'block') {
    return { label: 'Calendar block', color: ELIG_TOKENS.midnight };
  }

  switch (status) {
    case 'cancelled':
      return { label: 'Cancelled', color: ELIG_TOKENS.red };
    case 'confirmed':
      return { label: 'Confirmed', color: ELIG_TOKENS.green };
    case 'reschedule_requested':
      return { label: 'Reschedule requested', color: ELIG_TOKENS.amber };
    case 'scheduled':
      return { label: 'Scheduled', color: ELIG_TOKENS.gray };
  }
};

const getStatusTransitionForDisplayStatus = (
  appointment: Appointment,
  status: DisplayAppointmentStatus,
): { axis: 'slot' | 'response'; to: string } => {
  switch (status) {
    case 'cancelled':
      return { axis: 'slot', to: 'cancelled' };
    case 'confirmed':
      return { axis: 'response', to: 'confirmed' };
    case 'reschedule_requested':
      return { axis: 'response', to: 'reschedule_requested' };
    case 'scheduled':
      if (appointment.status === 'cancelled') {
        return { axis: 'slot', to: 'scheduled' };
      }
      return { axis: 'response', to: 'unconfirmed' };
  }
};

const getOptimisticPatchForStatusTransition = (
  appointment: Appointment,
  transition: { axis: 'slot' | 'response'; to: string },
): Partial<Appointment> => {
  const now = new Date().toISOString();
  const blockStatePatch = getBlockStatePatchForStatusTransition(transition);

  if (transition.axis === 'slot') {
    return {
      status: transition.to,
      status_changed_at: now,
      status_change_source: 'calendar_status_property',
      ...(transition.to === 'cancelled' ? { cancelled_at: now } : {}),
      ...(transition.to === 'scheduled'
        ? {
            cancelled_at: null,
            cancellation_source: null,
            cancellation_reason: null,
            cancelled_by_actor_type: null,
            cancelled_by_user_id: null,
          }
        : {}),
      ...(blockStatePatch && appointment.block_state ? { block_state: blockStatePatch } : {}),
    } as Partial<Appointment>;
  }

  return {
    confirmation_status: transition.to,
    confirmation_status_changed_at: now,
    confirmation_status_change_source: 'calendar_status_property',
    ...(blockStatePatch && appointment.block_state ? { block_state: blockStatePatch } : {}),
  } as Partial<Appointment>;
};

const getBlockStatePatchForStatusTransition = (
  transition: { axis: 'slot' | 'response'; to: string },
): Partial<NonNullable<Appointment['block_state']>> | undefined => {
  if (transition.axis === 'slot') {
    if (transition.to === 'cancelled') return { reality: 'cancelled' };
    if (transition.to === 'scheduled') return { reality: 'real' };
  }

  if (transition.to === 'cancelled') return { reality: 'cancelled' };
  if (transition.to === 'reschedule_requested') return { reality: 'rescheduling' };
  if (transition.to === 'confirmed') return { reality: 'real', qualifiers: { confirmed: true } };
  if (transition.to === 'unconfirmed') return { reality: 'real', qualifiers: { confirmed: false } };

  return undefined;
};
